import { OrderType } from 'models/Order'
import {
  ActivityLevels,
  BodyConditions,
  EatingHabits,
  HealthGoals,
  HealthIssues,
  PetAdultSizes,
  PetGenders,
  PetKinds,
  PetSizes,
  PetWeightChangeDesire,
  Proteins,
  DietPrior,
} from 'models/Pet'
import { UseMutationOptions, useMutation, useQueryClient } from 'react-query'
import { ApiClient } from 'services/ApiClient'
import { Api } from 'types/Api'
import { useAuth } from 'contexts/AuthContext'
import { OptInSettings } from '../../../../models/Client'
import { RawFeedingProductType, ProductVariantSnapshot } from '../../../../models/Product'

export type PetAttemptStep1 = {
  name: string
  kind: PetKinds
  gender: PetGenders
  isSpayed: boolean
  birthDate: string
  recommendedRawFeedingProductType?: RawFeedingProductType
  alternativeRawFeedingProductType?: RawFeedingProductType
  dietPrior?: DietPrior
  allergies?: Proteins[]
  isPuppy?: boolean
}

export type PetAttemptStep2 = {
  breed: string
  size?: PetSizes
  bodyCondition: BodyConditions
  weightChangeDesire?: PetWeightChangeDesire
  activityLevel: ActivityLevels
  weight: number
  adultWeight?: number
  isPuppy?: boolean
  adultSize?: PetAdultSizes
  recommendedRawFeedingProductType?: RawFeedingProductType
  alternativeRawFeedingProductType?: RawFeedingProductType
  dietPrior?: DietPrior
  allergies?: Proteins[]
}

export type PetAttemptStep3 = {
  allergies: Proteins[]
  email: string
  healthGoals?: HealthGoals[]
  healthIssues?: HealthIssues[]
  eatingHabits?: EatingHabits
  phoneNumber?: string
  dietPrior?: DietPrior
  isPuppy?: boolean
  mealPlan: {
    selectedProteins: Proteins[]
    rawDietPercentage: number
    rawFeedingProductType: RawFeedingProductType
  }
  recommendedRawFeedingProductType?: RawFeedingProductType
  alternativeRawFeedingProductType?: RawFeedingProductType
  optInSettings?: OptInSettings[]
}

export type PetAttemptProductPage = {
  formulaVersion?: string
  mealPlan: {
    selectedProteins: Proteins[]
    rawDietPercentage: number
    rawFeedingProductType: RawFeedingProductType
  }
  dailyDose?: number
  boxDuration?: number
}

export type PetAttemptRequest =
  | { ownerId?: string } & (
      | PetAttemptStep1
      | PetAttemptStep2
      | PetAttemptStep3
      | PetAttemptProductPage
    ) & { utmCampaign?: Record<string, any> }

export type PetAttempt = PetAttemptStep1 &
  PetAttemptStep2 &
  PetAttemptStep3 &
  PetAttemptProductPage & {
    ownerId: string
    promoCode?: string
    discount?: {
      code: string
      clientEmail: string
      orderType: OrderType
    }
    utmCampaign?: Record<string, any>
    creationConfiguration?: string
    tags?: string[]
  }

export type TrialBoxPetAttempt = PetAttemptStep1 &
  PetAttemptStep2 &
  PetAttemptStep3 & {
    selectedProteins: Proteins[]
    rawDietPercentage: number
    formulaVersion?: string
    ownerId: string
    promoCode?: string
    dailyDose?: number
    boxDuration?: number
    discount?: {
      code: string
      clientEmail: string
      orderType: OrderType
    }
    utmCampaign?: Record<string, any>
    clientId?: string
    phoneNumber?: string
    products?: ProductVariantSnapshot[]
    rawFeedingProductType: RawFeedingProductType
  }

export const FD_WEIGHT_MIN = 48

export const isFreezeDried = (submitData: any) => {
  if (submitData.mealPlan.rawFeedingProductType) {
    if (submitData.mealPlan.rawFeedingProductType === RawFeedingProductType.FROZEN) {
      return false
    }
  }
  if (submitData.recommendedRawFeedingProductType) {
    if (submitData.recommendedRawFeedingProductType === RawFeedingProductType.FROZEN) {
      return false
    }
  }
  return true
}

export const determineFeedingProductType = (submitData: any) => {
  if (
    submitData.dietPrior === DietPrior.FRESH_RAW_FOOD ||
    submitData.dietPrior === DietPrior.COOKED_FOOD ||
    submitData.dietPrior === DietPrior.WET_FOOD ||
    submitData.dietPrior === DietPrior.FREEZE_DRIED_RAW_FOOD ||
    submitData.dietPrior === DietPrior.OTHER ||
    submitData.allergies?.includes(Proteins.BEEF) ||
    submitData.allergies?.includes(Proteins.CHICKEN)
  ) {
    submitData.dietPrior &&
      (submitData.recommendedRawFeedingProductType = RawFeedingProductType.FROZEN)
    submitData.dietPrior &&
      (submitData.alternativeRawFeedingProductType = RawFeedingProductType.FREEZE_DRIED)
  } else {
    submitData.dietPrior &&
      (submitData.recommendedRawFeedingProductType = RawFeedingProductType.FREEZE_DRIED)
    submitData.dietPrior &&
      (submitData.alternativeRawFeedingProductType = RawFeedingProductType.FROZEN)
  }

  if (
    submitData.weight <= FD_WEIGHT_MIN ||
    submitData.isPuppy ||
    (submitData.allergies?.includes(Proteins.BEEF) &&
      submitData.allergies?.includes(Proteins.CHICKEN))
  ) {
    submitData.mealPlan &&
      (submitData.mealPlan.rawFeedingProductType = RawFeedingProductType.FROZEN)
    submitData.dietPrior &&
      (submitData.recommendedRawFeedingProductType = RawFeedingProductType.FROZEN)
    submitData.dietPrior && (submitData.alternativeRawFeedingProductType = undefined) // We do not set an alternative raw feeding product type for puppies or dogs with allergies to both beef and chicken
  }

  return submitData
}

export namespace SubmitPetAttempt {
  export type Response = PetAttempt
  export type Variables = PetAttemptRequest
  export type Error = Api.Error
  export type Options = UseMutationOptions<Response, Error, Variables>
}

export const useSubmitPetAttempt = (options?: SubmitPetAttempt.Options) => {
  const { clientId } = useAuth()
  const queryClient = useQueryClient()

  const mutationFn = async (data: SubmitPetAttempt.Variables) => {
    return ApiClient.post<SubmitPetAttempt.Response>('/v1/pet-attempts', {
      data: {
        ...data,
        ownerId: data.ownerId ?? clientId,
      },
    })
  }

  const mutation = useMutation<
    SubmitPetAttempt.Response,
    SubmitPetAttempt.Error,
    SubmitPetAttempt.Variables
  >(mutationFn, {
    ...options,
    onSuccess: (petAttempt, variables, context) => {
      queryClient.setQueryData(['pet-attempt', { ownerId: clientId }], petAttempt)
      options?.onSuccess?.(petAttempt, variables, context)
    },
  })

  return { ...mutation, submitPetAttempt: mutation.mutate }
}
